import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminIconComponent,
  AdminTableComponent,
  StatusBadgeComponent,
} from '../../../../shared/admin';
import { AuthService } from '../../../../services/auth.service';
import { FranchiseDetail, FranchisesService } from '../../../../shared/services/franchises.service';
import { PermissionService } from '../../../permissions/permission.service';

@Component({
  selector: 'app-franchise-detail-page',
  standalone: true,
  imports: [
    DatePipe,
    RouterLink,
    AdminCardComponent,
    AdminIconComponent,
    AdminButtonComponent,
    AdminTableComponent,
    StatusBadgeComponent,
  ],
  templateUrl: './franchise-detail-page.component.html',
  styleUrl: './franchise-detail-page.component.scss',
})
export class FranchiseDetailPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly franchisesService = inject(FranchisesService);
  private readonly authService = inject(AuthService);
  private readonly permissions = inject(PermissionService);

  readonly loading = signal(true);
  readonly detail = signal<FranchiseDetail | null>(null);

  readonly showManage = computed(() => this.permissions.canShow('franchises', 'statusChange'));
  readonly canManage = computed(() => this.permissions.canExecute('franchises', 'statusChange'));

  ngOnInit(): void {
    const franchiseId =
      this.route.snapshot.paramMap.get('franchiseId') ??
      this.route.snapshot.paramMap.get('id') ??
      '';
    this.franchisesService.getByFranchiseId(franchiseId).subscribe({
      next: (data) => {
        this.detail.set(data);
        this.loading.set(false);
      },
      error: () => {
        this.detail.set(null);
        this.loading.set(false);
      },
    });
  }

  toggleStatus(): void {
    const current = this.detail();
    if (!current) return;
    const next = !current.franchise.isActive;
    this.franchisesService.updateStatus(current.franchise.franchiseId ?? current.franchise.id, next).subscribe((updated) => {
      this.detail.update((d) =>
        d ? { ...d, franchise: { ...d.franchise, isActive: updated.isActive } } : d,
      );
    });
  }

  fullName(firstName?: string, lastName?: string): string {
    const label = [firstName, lastName].filter(Boolean).join(' ').trim();
    return label || 'Staff Member';
  }

  initials(firstName: string | undefined, lastName: string | undefined, email: string): string {
    const nameSeed = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (nameSeed) {
      return nameSeed
        .split(' ')
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('');
    }
    return email.slice(0, 2).toUpperCase();
  }
}
